import { ACCESS_TOKEN_COOKIE_NAME, COOKIE_DOMAIN, REFRESH_TOKEN_EXISTS_COOKIE_NAME } from '../constants/Constants';
import { isLocalhost } from '../helpers/Helpers';
import { LogDebug, LogMsg } from '../helpers/Logging';
import { getPublicServiceExternalUrlByK8sInternalUrl } from '../helpers/PoddedUrlTransformer';
import { validateServiceAccess } from '../helpers/AccessValidator';
import { IEmAuthCookies, ILoggerDetails, IEmServiceDetails } from '../types/types';

/**
 * @param {IEmServiceDetails} service - Details about the service using the middleware.
 * @param {string} emReAuthenticationServiceBaseUrl - The URL to redirect to for re-authentication
 * @param {ILoggerDetails} logger - The logger instance for logging information.
 */
interface IAuthVerificationExpressMiddlewareParams {
  //express: any;
  emService: IEmServiceDetails;
  emReAuthenticationServiceBaseUrl: string;
  logger?: ILoggerDetails;
}

/**
 * Middleware function to handle authentication in an Express application on PAGE requests only.
 *
 * !REQUIRES: Express application instance. This middleware function will use the express instance provided by the consuming service
 * !IMPORTANT: import `express` and `cookie-parser` in your service before using this middleware
 *
 * @param emService {IEmServiceDetails} - Details about the service using the middleware.
 * @param emReAuthenticationServiceBaseUrl - The URL to redirect to for re-authentication.
 * @param logger - The logger instance for logging information.
 * @returns A middleware function that checks for authentication cookies and redirects if necessary.
 *
 * The middleware checks for the presence of the 'em_rt' cookie and the absence of the 'em_at' cookie.
 * If this condition is met, it redirects the user to the re-authentication service URL with the original URL as a return parameter.
 * If the 'em_extid' cookie is present, it clears this cookie, indicating the user is logged out.
 * Finally, it calls the `next` function to pass control to the next middleware.
 */
export const authVerificationExpressMiddleware = ({
  emService, // TODO: per discussion with @Pratic & @Victor, it might be better to implement a callback function to have the consuming service handle the redirect in stead of hardcoding the redirect URL "calculated" here by destructuring current URL
  emReAuthenticationServiceBaseUrl,
  logger,
}: IAuthVerificationExpressMiddlewareParams) => {
  LogDebug(logger, `authVerificationExpressMiddleware called for ${JSON.stringify(emService)} service`);

  if (!emReAuthenticationServiceBaseUrl.trim()) {
    throw new Error(`'emReAuthenticationServiceBaseUrl' is required in authVerificationExpressMiddleware`);
  }

  return async (req: any, res: any, next: any) => {
    LogDebug(
      logger,
      `authVerificationExpressMiddleware :: starting to process request for ${
        req.originalUrl
      }, with emService: ${JSON.stringify(
        emService
      )} and emReAuthenticationServiceBaseUrl: ${emReAuthenticationServiceBaseUrl}`
    );

    if (req.headers['bot'] === 'true') {
      LogDebug(logger, `authVerificationExpressMiddleware :: skipping auth check for BOT request ${req.originalUrl}`);
      return next(); // if the request is from a bot, skip the auth check - since none of the logic here applies to bots
    }

    // Check if the request is for a page (and not for any other resource, like image, css, script etc..)
    const acceptHeader = req.headers['accept'] || '';
    if (!acceptHeader || acceptHeader === `*/*` || acceptHeader.includes('text/html')) {
      // proceed with the auth check
    } else {
      // skip the auth check for js/css/image etc. requests
      LogDebug(
        logger,
        `authVerificationExpressMiddleware :: skipping auth check for non-HTML (non-page) request:: ${
          req.originalUrl
        } with acceptHeader:: ${JSON.stringify(acceptHeader)} and cookies:: ${JSON.stringify(req.cookies)}`
      );
      return next();
    }

    LogDebug(logger, `authVerificationExpressMiddleware :: getting cookies from the request`);

    if (!req.cookies) {
      throw new Error(
        `req.cookies doesn't exist. Did you forget to import & use "cookie-parser" middleware within express app?`
      );
    }

    // Get cookies from the request
    const emAuthCookies: IEmAuthCookies = {
      em_rte: req.cookies[REFRESH_TOKEN_EXISTS_COOKIE_NAME], // refresh token "exists" (client-side)
      em_at: req.cookies[ACCESS_TOKEN_COOKIE_NAME], // access token - JWT
    };

    LogDebug(logger, `authVerificationExpressMiddleware :: emAuthCookies ${JSON.stringify(emAuthCookies)}`);

    try {
      // Reconstruction of the full URL from the request
      const podUrl = `${req.protocol}://${req.get('host')}${req.originalUrl}`;

      // Pod URLs are typically in the format: https://sources-public.staging.emarketer.com/sources, which need to be transformed to proper public URLs
      const fullExternalUrl = getPublicServiceExternalUrlByK8sInternalUrl(podUrl);

      LogDebug(logger, `authVerificationExpressMiddleware :: podded URL: ${podUrl}, full URL: ${fullExternalUrl}`);

      // Validate the service access
      const { redirectUrl, emCookiesToTrash } = await validateServiceAccess({
        originalUrl: fullExternalUrl,
        emService,
        emAuthCookies,
        emReAuthenticationServiceBaseUrl,
        logger,
      });

      // Clear cookies if needed
      if (emCookiesToTrash?.length > 0) {
        const cookieDomain = isLocalhost(fullExternalUrl) ? req.hostname : COOKIE_DOMAIN;

        emCookiesToTrash.forEach((cookie) => {
          LogDebug(
            logger,
            `authVerificationExpressMiddleware :: clearing cookie: ${cookie} for domain ${cookieDomain}`
          );
          // NOTE: need to provide `domain` and `path`, otherwise the cookie won't be cleared just by name
          req.cookies[cookie] && res.clearCookie(cookie, { path: '/', domain: cookieDomain });
        });
      }

      // Redirect if needed
      if (redirectUrl) {
        LogDebug(logger, `authVerificationExpressMiddleware :: redirecting to ${redirectUrl}`);

        // NOTE: (while 301 can throw off Akamai and Bots), the 307 (will not signal a "permanent" redirect). It is used to preserve the request method (GET, POST, etc.) and the request body. While 302 may change the method to GET, using 307 prevents accidental method changes and data loss during redirects.
        return res.redirect(307, redirectUrl);
      }

      next();
    } catch (error) {
      LogMsg(
        logger ? logger : { loggerInstance: console, logLevel: 'info' },
        `authVerificationExpressMiddleware :: error during validation: ${error}`,
        'error'
      );

      return next(error);
    }
  };
};
